import {
    DeclineReason,
    RepresentativeDeclineRequest
} from '@dtos/remote-i9-decline';
import { IRemoteRepresentativeService } from '@services/remote-representative-service';
import {
    action,
    computed,
    observable
} from 'mobx';
import { actionAsync } from 'mobx-utils';

export interface IDeclineModalStore {
    readonly showModal: boolean;
    readonly isReadyToSubmit: boolean;
    readonly selectedReason: DeclineReason;
    readonly reasonDescription: string;
    readonly wasValid: boolean;
    readonly hadErrorDeclining: boolean;
    readonly isLoading: boolean;

    openModal(): void;
    closeModal(): void;
    setReasonDescription(text: string): void;
    selectReason(reason: DeclineReason): void;
    initialise(): void;
    requestDeclineAsync(): Promise<boolean>;
    validate(): boolean;
}

export class DeclineModalStore implements IDeclineModalStore {
    @observable
    showModal: boolean = false;
    @observable
    selectedReason: DeclineReason = DeclineReason.OTHER;
    @observable
    reasonDescription: string = '';
    @observable
    wasValid: boolean = true;
    @observable
    hadErrorDeclining: boolean = false;
    @observable
    isLoading: boolean = false;

    constructor(
        private remoteRepresentativeService: IRemoteRepresentativeService
    ) {

    }

    @computed
    get isReadyToSubmit(): boolean {
        if (!this.selectedReason) {
            return false;
        }

        const otherAndTextFilled: boolean = (
            this.selectedReason === DeclineReason.OTHER &&
            this.reasonDescription?.length > 0
        );

        const hasSelectedStandardReason: boolean = this.selectedReason !== DeclineReason.OTHER;

        return hasSelectedStandardReason || otherAndTextFilled;
    }

    @action.bound
    validate(): boolean {
        this.wasValid = this.isReadyToSubmit;
        return this.wasValid;
    }

    @action.bound
    initialise(): void {
        this.showModal = false;
        this.selectedReason = DeclineReason.OTHER;
        this.reasonDescription = '';
        this.hadErrorDeclining = false;
        this.wasValid = true;
        this.isLoading = false;
    }

    @action.bound
    selectReason(reason: DeclineReason): void {
        this.selectedReason = reason;
    }

    @action.bound
    setReasonDescription(text: string): void {
        this.reasonDescription = text;
    }

    @actionAsync
    requestDeclineAsync = async (): Promise<boolean> => {
        try {
            this.isLoading = true;

            this.validate();
            if (!this.wasValid) {
                return false;
            }

            this.hadErrorDeclining = false;
            const declineI9Request: RepresentativeDeclineRequest = {
                declineReasonText: this.reasonDescription,
                // unsure if this will be made generic for the other sections
                section3DeclineReason: this.selectedReason
            };

            const response = await this.remoteRepresentativeService.declineI9Async(declineI9Request);
            if (!response) {
                this.hadErrorDeclining = true;
                return false;
            }

            this.closeModal();

            return true;
        } catch (error) {
            console.error(error);
            this.hadErrorDeclining = true;
            return false;
        } finally {
            this.isLoading = false;
        }
    };

    @action.bound
    closeModal(): void {
        this.showModal = false;
    }

    @action.bound
    openModal(): void {
        this.initialise();
        this.showModal = true;
    }
}



// WEBPACK FOOTER //
// ./src/stores/modal/decline-modal-store.ts